import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

// ─── Patch navigator.locks at module level ───────────────────────────────────
// Supabase's GoTrueClient acquires a Web Lock for every token refresh.
// In React Strict Mode / hot-reload, two concurrent calls race for the same
// lock; one steals it and throws an AbortError. We bypass the lock entirely
// by replacing navigator.locks.request with a no-op that just runs the
// callback immediately. The singleton client pattern already prevents
// concurrent access, so the lock is unnecessary.
if (
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  navigator.locks &&
  !(navigator.locks as any).__sb_patched__
) {
  (navigator.locks as any).__sb_patched__ = true;
  const _origRequest = navigator.locks.request.bind(navigator.locks);
  (navigator.locks as any).request = (
    name: string,
    optionsOrCallback: any,
    maybeCallback?: any
  ): Promise<any> => {
    // Supabase calls: navigator.locks.request(name, { steal: true }, fn)
    // or: navigator.locks.request(name, fn)
    const fn = typeof maybeCallback === 'function' ? maybeCallback
              : typeof optionsOrCallback === 'function' ? optionsOrCallback
              : null;
    const options = typeof optionsOrCallback === 'object' && optionsOrCallback !== null ? optionsOrCallback : {};
    // Intercept any lock that uses steal:true OR any sb-/lock:sb- prefixed lock
    const isSbLock = typeof name === 'string' && (
      name.startsWith('lock:sb-') ||
      name.startsWith('sb-') ||
      name.includes('-auth-token') ||
      name.includes('-auth-lock')
    );
    const isStealLock = options.steal === true || options.ifAvailable === true;
    if (fn && (isSbLock || isStealLock)) {
      // Bypass the lock entirely — just run the callback directly.
      return Promise.resolve().then(() => fn(null)).catch((err: any) => {
        const msg = (err?.message ?? '').toLowerCase();
        if (err?.name === 'AbortError' || msg.includes('lock broken') || msg.includes('steal') || msg.includes('aborted')) {
          return undefined;
        }
        throw err;
      });
    }
    // For all other locks, use the original implementation
    return _origRequest(name, optionsOrCallback, maybeCallback);
  };
}

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined' ? [] :
  document.cookie.split(';').filter(Boolean).map((c) => {
    const eqIdx = c.trim().indexOf('=');
    const name = eqIdx >= 0 ? c.trim().slice(0, eqIdx) : c.trim();
    const value = eqIdx >= 0 ? decodeURIComponent(c.trim().slice(eqIdx + 1)) : '';
    return { name: name.trim(), value };
  }).filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch { return []; }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};
const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = ['', host, host ? `.${host}` : ''].filter(Boolean);

  const variants = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=None; Secure',
    'Path=/; SameSite=None; Secure; Partitioned',
  ];

  variants.forEach((attrs) => {
    document.cookie = `${name}=; Max-Age=0; ${attrs}`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
    });
  });
};

const getToken = () =>
  (canUseCookies() ? fromCookies() : fromStorage())
    .find((c) => c.name.includes('auth-token'))?.value ?? null;

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const token = getToken();
    const url = typeof input === 'string' ? input
      : input instanceof URL ? input.href
      : (input as Request).url;
    if (token && (url.startsWith('/') || url.startsWith(window.location.origin))) {
      init = { ...(init || {}), headers: { ...(init?.headers || {}), 'x-sb-token': token } };
    }
    return orig(input, init);
  };

  // Helper: detect any lock/abort related error string
  function isLockOrAbortMsg(msg: string): boolean {
    const m = msg.toLowerCase();
    return (
      m.includes('aborted') ||
      m.includes('lock broken') ||
      m.includes('steal') ||
      m.includes('aborterror') ||
      m.includes('web lock') ||
      m.includes('abortcontroller') ||
      m.includes('lock manager') ||
      m.includes('was not released') ||
      m.includes('orphaned lock') ||
      m.includes('forcefully acquiring') ||
      m.includes('lock:sb-')
    );
  }

  // Intercept console.error to suppress lock/abort and token errors
  const origConsoleError = console.error.bind(console);
  console.error = (...args: any[]) => {
    const msg = args.map((a) => (typeof a === 'string' ? a : a?.message ?? a?.name ?? '')).join(' ');

    const hasAbortName = args.some((a) => a?.name === 'AbortError' || a instanceof DOMException);
    if (hasAbortName || isLockOrAbortMsg(msg)) return;

    if (
      msg.toLowerCase().includes('invalid refresh token') ||
      msg.toLowerCase().includes('already used') ||
      msg.toLowerCase().includes('refresh_token_not_found')
    ) {
      return;
    }

    origConsoleError(...args);
  };

  // Intercept console.warn — suppress lock warnings from Supabase
  const origConsoleWarn = console.warn.bind(console);
  console.warn = (...args: any[]) => {
    const msg = args.map((a) => (typeof a === 'string' ? a : a?.message ?? a?.name ?? '')).join(' ');
    const hasAbortName = args.some((a) => a?.name === 'AbortError' || a instanceof DOMException);
    if (hasAbortName || isLockOrAbortMsg(msg)) return;
    origConsoleWarn(...args);
  };

  // Intercept console.log — suppress lock/abort noise from Supabase
  const origConsoleLog = console.log.bind(console);
  console.log = (...args: any[]) => {
    const msg = args.map((a) => (typeof a === 'string' ? a : a?.message ?? a?.name ?? '')).join(' ');
    const hasAbortName = args.some((a) => a?.name === 'AbortError' || a instanceof DOMException);
    if (hasAbortName || isLockOrAbortMsg(msg)) return;
    origConsoleLog(...args);
  };

  // Global unhandledrejection: suppress AbortError
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event?.reason;
    const name: string = reason?.name ?? '';
    const msg: string = reason?.message ?? '';
    if (name === 'AbortError' || reason instanceof DOMException || isLockOrAbortMsg(msg) || isLockOrAbortMsg(name)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
}

/**
 * Forcefully clears all Supabase auth tokens from cookies and localStorage
 * WITHOUT acquiring a Web Lock.
 */
export function forceSignOut() {
  if (typeof window === 'undefined') return;

  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX) || k.startsWith('sb-'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}

  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name.startsWith('sb-') || name.startsWith(PFX)) {
        const host = window.location.hostname;
        const domains = ['', host, `.${host}`].filter(Boolean);
        const variants = [
          'Path=/; SameSite=Lax',
          'Path=/; SameSite=None; Secure',
          'Path=/; SameSite=None; Secure; Partitioned',
        ];
        variants.forEach((attrs) => {
          document.cookie = `${name}=; Max-Age=0; ${attrs}`;
          domains.forEach((domain) => {
            document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
          });
        });
      }
    });
  } catch {}

  try {
    delete (window as any).__supabase_client__;
  } catch {}
}

export function createClient() {
  if (typeof window !== 'undefined' && (window as any).__supabase_client__) {
    return (window as any).__supabase_client__;
  }

  const client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => canUseCookies() ? fromCookies() : fromStorage(),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value ? setCookie(name, value, options)
                    : deleteCookie(name)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value ? localStorage.setItem(`${PFX}${name}`, value)
                      : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
      auth: {
        // Belt-and-suspenders: also bypass via the auth config lock option
        // (in addition to the navigator.locks patch at module level above)
        lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => fn(),
        onAuthError: (error: any) => {
          const msg: string = (error?.message ?? '').toLowerCase();
          const isInvalidToken =
            msg.includes('invalid refresh token') ||
            msg.includes('already used') ||
            msg.includes('refresh_token_not_found') ||
            error?.code === 'refresh_token_not_found';

          if (isInvalidToken && typeof window !== 'undefined') {
            if (window.location.pathname !== '/login' && !(window as any).__sb_signing_out__) {
              (window as any).__sb_signing_out__ = true;
              delete (window as any).__supabase_client__;
              forceSignOut();
              window.location.href = '/login';
            }
          }
        },
      },
    }
  );

  if (typeof window !== 'undefined') {
    (window as any).__supabase_client__ = client;
  }

  return client;
}